/**
 * Mã đăng nhập của nhà cung cấp — luật thuần, không db/IO.
 *
 * Khách chốt 15/09/2026: mỗi nhà cung cấp CHỈ MỘT người đăng nhập, và tên đăng
 * nhập là "số id chúng ta cung cấp". Khách nói thêm — đúng — rằng để mã mặc
 * định thì "hơi không được bảo mật", nên phải cho đặt mã riêng, và mã tự đặt
 * KHÔNG được trùng với nhà cung cấp khác.
 *
 * Vì sao không dùng thẳng `suppliers.id`: nó là UUID
 * (`21aceca0-9ad0-415c-ae79-df33e82fbe6b`). Không ai đọc được con số đó qua
 * điện thoại, càng không gõ lại được. Phải có một mã ngắn, riêng cho việc đăng
 * nhập.
 *
 * Vì sao mã mặc định KHÔNG đánh số tuần tự (SUP001, SUP002…): đánh số tuần tự
 * là mời người ta dò. Ai có một mã là đoán được mọi mã còn lại, rồi thử mật
 * khẩu lần lượt với từng nhà cung cấp. Đó chính là nỗi lo khách vừa nêu. Mã
 * sinh ngẫu nhiên thì biết một mã không suy ra được mã nào khác.
 *
 * Mã KHÔNG phải là bí mật và không được coi là bí mật: nó là TÊN ĐĂNG NHẬP,
 * mật khẩu mới là thứ chứng minh danh tính. Sinh ngẫu nhiên chỉ để bịt đường
 * dò hàng loạt, không thay được mật khẩu.
 */

/**
 * Bảng chữ cái không có ký tự dễ đọc nhầm.
 *
 * Bỏ `0`/`O`, `1`/`I`/`L`: mã này được đọc qua điện thoại và chép tay giữa hai
 * công ty nói hai thứ tiếng. Một mã đọc nhầm là một cuộc gọi hỗ trợ.
 */
const ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";

/** Độ dài phần ngẫu nhiên. 31^6 ≈ 887 triệu — dò mù là vô vọng. */
const RANDOM_LENGTH = 6;

export const PORTAL_LOGIN_PREFIX = "NCC-";

/**
 * Mã hợp lệ: bắt đầu bằng chữ hoặc số, sau đó chữ/số/gạch ngang/gạch dưới.
 *
 * Không cho khoảng trắng và không cho `@`: có `@` thì ô đăng nhập sẽ hiểu là
 * email, và hai cách hiểu cho cùng một chuỗi là chỗ để sai.
 */
export const PORTAL_LOGIN_ID_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{2,39}$/;

/**
 * Dạng chuẩn hoá để SO SÁNH và LƯU: bỏ khoảng trắng hai đầu, hạ chữ thường.
 *
 * Không phân biệt hoa thường, vì người gõ lại một mã chép tay sẽ gõ tuỳ hứng —
 * và vì `ncc-a2b3c4` với `NCC-A2B3C4` là hai nhà cung cấp khác nhau thì đó là
 * cái bẫy, không phải tính năng.
 */
export function normalizePortalLoginId(v: string | null | undefined): string {
  return (v ?? "").trim().toLowerCase();
}

/** Ô đăng nhập đang nhận một email hay một mã nhà cung cấp. */
export function isEmailLike(v: string): boolean {
  return v.includes("@");
}

export type PortalLoginIdProblem = "portal_login_id_format" | "portal_login_id_reserved";

/**
 * Mã tự đặt có dùng được không — KHÔNG kiểm trùng ở đây.
 *
 * Trùng hay không là câu hỏi của cơ sở dữ liệu, không phải của một hàm thuần;
 * tầng nghiệp vụ hỏi kho rồi mới kết luận. Tách ra để phần luật này kiểm được
 * mà không cần db.
 */
export function portalLoginIdProblem(raw: string): PortalLoginIdProblem | null {
  const v = (raw ?? "").trim();
  if (!PORTAL_LOGIN_ID_RE.test(v)) return "portal_login_id_format";
  // Chuỗi trông như email đi vào đây là người dùng nhầm ô, phải nói ra.
  if (isEmailLike(v)) return "portal_login_id_format";
  return null;
}

/**
 * Sinh một mã mặc định.
 *
 * `random` là THAM SỐ BẮT BUỘC, không có mặc định gọi `crypto` toàn cục: gói
 * `shared` dùng chung cho Worker và trình duyệt và cố ý không phụ thuộc vào
 * bất kỳ global nào của môi trường nào. Bên gọi truyền
 * `n => crypto.getRandomValues(new Uint8Array(n))`; bài kiểm truyền một dãy
 * biết trước để kết quả xác định được.
 */
export function generatePortalLoginId(random: (n: number) => Uint8Array): string {
  const bytes = random(RANDOM_LENGTH);
  let out = "";
  for (let i = 0; i < RANDOM_LENGTH; i++) out += ALPHABET[bytes[i]! % ALPHABET.length];
  return `${PORTAL_LOGIN_PREFIX}${out}`;
}
